import React, { useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useUser } from '../hooks/useUser';
import { MeCollection } from '../swagger/collections-identity';
import { setUser } from '../store/userSlice';
import { auth } from '../app';
import { Unauthorized } from '@ydistri/ds';
import { useAppDispatch } from '../store';

enum ROLE {
  SUPER_ADMIN = 'SUPER_ADMIN',
}

interface PrivateRouteProps {
  component: React.ComponentType;
  path?: string;
  roles?: ROLE[];
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ component, roles = [] }) => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const location = useLocation();
  const isAuthenticated = auth.isAuthenticated();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    MeCollection.getMe()
      .then(({ data }) => {
        dispatch(setUser(data.data));
      })
      // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable -- we know the type
      .catch((error: Error) => {
        // eslint-disable-next-line no-console -- We want the output
        console.log(`Error getting information about current user: ${error.message}`);
      });
  }, [dispatch, isAuthenticated]);

  const userHasRequiredRole = useMemo(
    () =>
      roles.length === 0 ||
      roles.filter(requiredRole => {
        switch (requiredRole) {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- I know there is only one case
          case ROLE.SUPER_ADMIN:
            return user?.isSuperUser;
          default:
            return false;
        }
      }).length > 0,
    [roles, user?.isSuperUser],
  );

  if (isAuthenticated && userHasRequiredRole) {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- We want to use the component as a component
    const RouteComponent = component;
    return <RouteComponent />;
  }

  if (isAuthenticated && !userHasRequiredRole) {
    return <Unauthorized />;
  }

  // The guarded location rides along so the login screen can return here (returnTo.ts) - a deep
  // link, or the MCP approval page a browser was sent to before the user was logged in.
  return <Navigate to="/login" replace state={{ from: location }} />;
};

export default PrivateRoute;
